import {
    E_RISK_TYPE,
    IRootStoreExporter,
    GLOBAL_INDEXES,
    IColorsStackSerializedValuesData,
} from '../interfaces';
import { makeAutoObservable } from 'mobx';
import ColorStack from '../utils/ColorStack';
import * as _ from 'lodash';
import KeyValueStore from './KeyValueStore';
import { isNil, orderBy, first, get, includes, map, find } from 'lodash/fp';
import {
    IActiveContingentRisk,
    IActiveDimension,
    IActiveDimensionAPI,
    IAPIActiveClientFacingIndicator,
    RISK_CHART_MODES,
} from 'services/APIService';
import ChartScoresService from '../services/ChartScoresService';
import { APICacheInstance } from '../services/lib/APICacheInstance';

interface IRisksStoreOptionalState {
    currencyOn?: boolean;
    currentRisksType?: E_RISK_TYPE;
    isMinimized?: boolean;
    projectionDataOn?: boolean;
    riskMarkersOn?: boolean;
    riskChartMode?: RISK_CHART_MODES;
    showGeoquantLine?: boolean;
    currencyCountry?: number[];
    macroeconomicsCountry?: number[];
    activeMacroeconomicsIndicator?: string;
}

export interface IRiskStoreSerializedColors {
    dimensionsStack: Array<IColorsStackSerializedValuesData<number>>;
    clientFacingIndicatorsStack: Array<
        IColorsStackSerializedValuesData<number>
    >;
}
export class RisksStore implements IRootStoreExporter {
    public currencyOn: boolean;
    public currencyCountry: number[];
    public macroeconomicsCountry: number[];
    public activeMacroeconomicsIndicator: string;
    public projectionDataOn: boolean;
    public riskMarkersOn: boolean;
    public riskChartMode: RISK_CHART_MODES;
    public currentRisksType: E_RISK_TYPE;
    public isMinimized: boolean;
    public storesByRiskType: Map<
        E_RISK_TYPE,
        KeyValueStore<number>
    > = new Map();
    public homepageRiskId: number;
    public dimensionsStack: ColorStack<number>;
    public clientFacingIndicatorsStack: ColorStack<number>;
    public activeGlobalIndicators: GLOBAL_INDEXES[] = [];
    public dimensions: IActiveDimensionAPI = null;
    public allDimensions: IActiveDimension[] = [];
    public contingentRisks: IActiveContingentRisk[] = [];
    public clientFacingIndicators: IAPIActiveClientFacingIndicator;

    private dimensionsPermissions: { [key: number]: boolean };
    private CFIPermissions: { [key: number]: boolean };
    private defaultDimensionsStack: ColorStack<number>;
    private contingentPermissions: { [key: number]: boolean };
    private defaultClientFacingIndicatorsStack: ColorStack<number>;
    constructor() {
        makeAutoObservable(this);
        this.toInitialState();
        this.defaultDimensionsStack = new ColorStack<number>();
        this.defaultClientFacingIndicatorsStack = new ColorStack<number>();
        this.dimensionsStack = this.defaultDimensionsStack;
        this.clientFacingIndicatorsStack = this.defaultClientFacingIndicatorsStack;
        this.currencyCountry = [];
        this.macroeconomicsCountry = [];
        this.activeMacroeconomicsIndicator = '';
    }

    public serializeColors(): IRiskStoreSerializedColors {
        return {
            dimensionsStack: this.dimensionsStack.serialize(),
            clientFacingIndicatorsStack: this.clientFacingIndicatorsStack.serialize(),
        };
    }

    public setStacks(
        dimensionsStack: ColorStack<number>,
        clientFacingIndicatorsStack: ColorStack<number>
    ) {
        this.dimensionsStack = dimensionsStack;
        this.clientFacingIndicatorsStack = clientFacingIndicatorsStack;
    }

    public setDefaultStacks() {
        this.dimensionsStack = this.defaultDimensionsStack;
        this.clientFacingIndicatorsStack = this.defaultClientFacingIndicatorsStack;
    }

    public setMinimized(value: boolean) {
        this.setState({
            isMinimized: value,
        });
    }

    public setCurrencyCountry(id: number) {
        const currencyCountry = this.currencyCountry[0] === id ? [] : [id];
        this.setState({
            currencyOn: currencyCountry.length > 0,
            currencyCountry,
        });
    }

    public setCurrencyCountries(ids: number[]) {
        this.setState({
            currencyOn: ids.length > 0,
            currencyCountry: [...ids],
        });
    }

    public cleanCurrencyCountry() {
        this.setState({
            currencyOn: false,
            currencyCountry: [],
        });
    }

    public setIsCurrencyOn(value: boolean) {
        this.setState({
            currencyOn: value,
        });
    }
    public getCurrencyCountry(): number[] {
        return this.currencyCountry;
    }

    public getIsCurrencyOn(): boolean {
        return this.currencyOn;
    }

    public setMacroeconomicsCountry(countryId: number, indicator: string) {
        if (this.activeMacroeconomicsIndicator) {
            ChartScoresService.clearCache();
            APICacheInstance.deleteValue(`^GlobalIndex::${this.activeMacroeconomicsIndicator}`);
        }
        
        this.setState({
            macroeconomicsCountry: [countryId],
            activeMacroeconomicsIndicator: indicator,
        });
    }

    public cleanMacroeconomicsCountry() {
        if (this.activeMacroeconomicsIndicator) {
            ChartScoresService.clearCache();
            APICacheInstance.deleteValue(`^GlobalIndex::${this.activeMacroeconomicsIndicator}`);
        }
        
        this.setState({
            macroeconomicsCountry: [],
            activeMacroeconomicsIndicator: '',
        });
    }

    public updateDimensions(data: IActiveDimensionAPI) {
        this.dimensions = data;
    }

    public updateAllDimensions(data: IActiveDimension[]) {
        this.allDimensions = data;
    }
    public updateClientFacingIndicators(
        data: IAPIActiveClientFacingIndicator
    ) {
        this.clientFacingIndicators = data;
    }
    public updateContingentRisks(data: IActiveContingentRisk[]) {
        this.contingentRisks = data;
    }

    public setState(state: IRisksStoreOptionalState) {
        Object.assign(this, state)
    }

    public toInitialState() {
        this.clear();
        const currentRisksType = isNil(this.currentRisksType)
            ? E_RISK_TYPE.DIMENSIONS
            : this.currentRisksType;

        this.setState({
            currentRisksType,
            currencyOn: false,
            isMinimized: false,
            projectionDataOn: false,
            riskChartMode: RISK_CHART_MODES.RISK_AND_PULSE,
            currencyCountry: [],
            macroeconomicsCountry: [],
            activeMacroeconomicsIndicator: '',
        });
    }
    public setDisabledRisks(
        dimensions: { [key: number]: boolean },
        CFI: { [key: number]: boolean },
        contingent: { [key: number]: boolean }
    ) {
        this.dimensionsPermissions = dimensions;
        this.CFIPermissions = CFI;
        this.contingentPermissions = contingent;
    }

    public get showGeoquantLine(): boolean {
        return includes(this.riskChartMode, [RISK_CHART_MODES.RISK, RISK_CHART_MODES.RISK_AND_PULSE]);
    }

    public get showPulseData(): boolean {
        return includes(this.riskChartMode, [
            RISK_CHART_MODES.PULSE,
            RISK_CHART_MODES.RISK_AND_PULSE,
        ]);
    }

    public get currentList(): number[] {
        if (!this.currentStore) {
            return [];
        }
        return this.currentStore.getValues();
    }

    public get currentStore(): KeyValueStore<number> {
        return this.storesByRiskType.get(this.currentRisksType);
    }

    public get isClientFacingIndicators(): boolean {
        return this.currentRisksType === E_RISK_TYPE.CLIENT_FACING_INDICATORS;
    }

    public setCurrentRiskType(risk: E_RISK_TYPE) {
        this.currentRisksType = risk;
    }

    public toggleRisk(riskID: number, ...ids: number[]) {
        this.currentStore.toggleValue(riskID, ...ids);
    }

    public resetRisksToDefault() {
        this.currentStore.overrideValues(this.currentStore.getDefaultValue());
    }

    public clear() {
        this.clearGlobalIndicator();
        this.cleanCurrencyCountry();
    }

    public get colorStack(): ColorStack<number> {
        return this.currentRisksType === E_RISK_TYPE.DIMENSIONS
            ? this.dimensionsStack
            : this.clientFacingIndicatorsStack;
    }

    public customExport() {
        const stack = this.colorStack;
        return {
            currencyOn: this.currencyOn,
            currentRisksType: this.currentRisksType,
            projectionDataOn: this.projectionDataOn,
            riskChartMode: this.riskChartMode,
            selectedRisks: this.currentList.map(id => {
                return {
                    id,
                    color: stack.color(id),
                };
            }),
        };
    }
    public checkIfRiskDisabled(
        riskType: E_RISK_TYPE,
        riskID: number[]
    ) {
         let permissionsObject;
          switch (riskType) {
              case E_RISK_TYPE.CLIENT_FACING_INDICATORS:
                  permissionsObject = this.CFIPermissions;
                  break;
              case E_RISK_TYPE.DIMENSIONS:
                  permissionsObject = this.dimensionsPermissions;
                  break;
              case E_RISK_TYPE.CONTINGENT:
                  permissionsObject = this.contingentPermissions;
                  break;
          }

          for (const risk of riskID) {
              if (!permissionsObject[risk]) {
                  return true;
              }
          }
          return false;
    }

    public toggleIsMultiValue(override?: boolean) {
        this.currentStore.toggleIsMultiValue(override);
    }

    public toggleGlobalIndicator(indicator: GLOBAL_INDEXES) {
        if (this.activeGlobalIndicators.includes(indicator)) {
            this.activeGlobalIndicators = this.activeGlobalIndicators.filter(
                i => i !== indicator
            );
            return;
        }

        this.activeGlobalIndicators = [indicator];
    }
    public clearGlobalIndicator() {
        this.activeGlobalIndicators.length = 0;
    }

    public overrideGlobalIndicators(indicators: GLOBAL_INDEXES[]) {
        this.activeGlobalIndicators = [...indicators];
    }

    public customRestore(data: {
        selectedRisks: Array<{ id: number; color: string }>;
    }) {
        this.clear();
        const state = { ...data };
        _.forEach(state.selectedRisks, ({ id }) => {
            this.toggleRisk(id);
        });
        delete state.selectedRisks;
    }

    private getFirstRiskId = (risks: object[]) => {
        return get('id'), first(risks);
    }

    private getFirstPermissionedRiskId = (risks: object[], permObject: {[key: number]: boolean} = {}) => {
        const ids = map(get('id'), risks);
        return find(
            (riskId: number) => !!permObject[riskId],
            ids
        );
    }

    public initRisksByType() {
        const firstDimensionIdWithPerm =
            this.getFirstPermissionedRiskId(
                this.dimensions.flatten,
                this.dimensionsPermissions
            ) || this.getFirstRiskId(this.dimensions.flatten);

        const cfi = orderBy(['order'], ['asc'], this.clientFacingIndicators);
        const firstCFIIdWithPerm =
            this.getFirstPermissionedRiskId(cfi, this.CFIPermissions) ||
            this.getFirstRiskId(cfi);
        const firstContingentRiskIdWithPerm =
            this.getFirstPermissionedRiskId(
                this.contingentRisks,
                this.contingentPermissions
            ) || this.getFirstRiskId(this.contingentRisks);

        this.storesByRiskType = new Map([
            [
                E_RISK_TYPE.DIMENSIONS,
                new KeyValueStore(true, Number, true, firstDimensionIdWithPerm),
            ],
            [
                E_RISK_TYPE.CONTINGENT,
                new KeyValueStore(true, Number, true, firstContingentRiskIdWithPerm),
            ],
            [
                E_RISK_TYPE.CLIENT_FACING_INDICATORS,
                new KeyValueStore(
                    true,
                    Number,
                    true,
                    firstCFIIdWithPerm
                ),
            ],
        ]);
        this.homepageRiskId = firstDimensionIdWithPerm;
    }
}

export default new RisksStore();